import { isDiarizationFragmentsEnabled } from '@app/modules/fragments/helpers/is-transcription-enabled';
import { useIsVideoFragment } from '@app/modules/fragments/hooks/is-video-fragment';
import { Station } from '@mi-front/core';
import { Grid } from '@mui/material';
import { Fragment } from '../../../../types';
import { useFragmentCtx } from '../../context/fragment-ctx';
import { AudioFragmentChunksList } from '../audio/media-player/AudioFragmentChunksList';
import { AudioFragmentMedia } from '../audio/media-player/AudioFragmentMedia';
import { TranscriptionView } from '../transcription/TranscriptionView';
import {
  VideoFragmentMedia,
  VideoFragmentPlayerContent,
} from '../video/media-player/VideoFragmentMedia';
import { FragmentInfoBlock } from './FragmentInfoBlock';
import { MediaInProgress } from './MediaInProgress';

/**
 * Fragment view: media (video or audio) and metadata in an 8/4 grid,
 * with transcription in a full-width row below both. No drawer or side panel.
 */
export function FragmentInfoView(props: { fragment: Fragment; station: Station }) {
  const { fragment, station } = props;
  const isVideo = useIsVideoFragment(useFragmentCtx());
  const showTranscription = isDiarizationFragmentsEnabled(station);
  const isMediaReady = fragment.media.status === 'READY';

  if (!isMediaReady) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MediaInProgress variant={isVideo ? 'video' : 'audio'} />
        </Grid>
        <Grid item xs={12} md={4}>
          <FragmentInfoBlock fragment={fragment} />
        </Grid>
      </Grid>
    );
  }

  if (isVideo) {
    return (
      <VideoFragmentMedia>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <VideoFragmentPlayerContent />
          </Grid>
          <Grid item xs={12} md={4}>
            <FragmentInfoBlock fragment={fragment} />
          </Grid>
          {showTranscription && (
            <Grid item xs={12}>
              <TranscriptionView fragmentId={fragment.id} />
            </Grid>
          )}
        </Grid>
      </VideoFragmentMedia>
    );
  }

  return (
    <AudioFragmentMedia>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <AudioFragmentChunksList />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <FragmentInfoBlock fragment={fragment} />
        </Grid>
        {showTranscription && (
          <Grid item xs={12}>
            <TranscriptionView fragmentId={fragment.id} />
          </Grid>
        )}
      </Grid>
    </AudioFragmentMedia>
  );
}
